import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Instagram, Youtube, Twitter, Mail, MessageCircle } from "lucide-react";

const PRESETS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, source: "whatsapp", medium: "chat", campaign: "broadcast" },
  { key: "instagram", label: "Instagram", icon: Instagram, source: "instagram", medium: "social", campaign: "bio" },
  { key: "youtube", label: "YouTube", icon: Youtube, source: "youtube", medium: "video", campaign: "description" },
  { key: "twitter", label: "Twitter / X", icon: Twitter, source: "twitter", medium: "social", campaign: "post" },
  { key: "email", label: "Email", icon: Mail, source: "email", medium: "newsletter", campaign: "launch" },
];

export default function UtmBuilder({ slug }) {
  const [source, setSource] = useState("whatsapp");
  const [medium, setMedium] = useState("chat");
  const [campaign, setCampaign] = useState("broadcast");
  const [content, setContent] = useState("");

  const baseUrl = `${window.location.origin}/w/${slug}`;

  const finalUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (source) p.set("utm_source", source);
    if (medium) p.set("utm_medium", medium);
    if (campaign) p.set("utm_campaign", campaign);
    if (content) p.set("utm_content", content);
    const q = p.toString();
    return q ? `${baseUrl}?${q}` : baseUrl;
  }, [source, medium, campaign, content, baseUrl]);

  const applyPreset = (p) => {
    setSource(p.source); setMedium(p.medium); setCampaign(p.campaign);
  };

  const copy = () => {
    navigator.clipboard.writeText(finalUrl);
    toast.success("Tracked link copied");
  };

  return (
    <div className="vp-card p-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="vp-label">Share & track</div>
          <div className="font-display text-xl font-bold mt-1">UTM link builder</div>
          <p className="text-sm text-[#4A4D57] mt-1">
            Every registration is auto-tagged to the source — visible on the Analytics page.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => applyPreset(p)}
            data-testid={`utm-preset-${p.key}`}
            className={
              "vp-chip transition-colors duration-150 hover:bg-[#F6F7FA] " +
              (source === p.source ? "vp-chip-accent" : "")
            }
          >
            <p.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {p.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mt-5">
        <div>
          <div className="vp-label mb-1.5">Source</div>
          <input className="vp-input" value={source} onChange={(e) => setSource(e.target.value)} data-testid="utm-source" />
        </div>
        <div>
          <div className="vp-label mb-1.5">Medium</div>
          <input className="vp-input" value={medium} onChange={(e) => setMedium(e.target.value)} data-testid="utm-medium" />
        </div>
        <div>
          <div className="vp-label mb-1.5">Campaign</div>
          <input className="vp-input" value={campaign} onChange={(e) => setCampaign(e.target.value)} data-testid="utm-campaign" />
        </div>
        <div>
          <div className="vp-label mb-1.5">Content (opt)</div>
          <input className="vp-input" value={content} onChange={(e) => setContent(e.target.value)} placeholder="v1-story" data-testid="utm-content" />
        </div>
      </div>

      <div className="mt-5 flex items-stretch gap-2">
        <div className="flex-1 vp-input font-mono text-xs break-all flex items-center min-h-[48px]" data-testid="utm-final-url">
          {finalUrl}
        </div>
        <button onClick={copy} data-testid="utm-copy-btn" className="vp-btn-primary inline-flex items-center gap-2 shrink-0">
          <Copy className="w-4 h-4" /> Copy
        </button>
      </div>

      <div className="mt-3 text-xs text-[#747680] flex items-center gap-1">
        <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        Analytics groups these under <b className="text-[#0B0D17]">Sources</b> — one row per <code className="text-[#0B5CFF]">utm_source</code>.
      </div>
    </div>
  );
}
